import React, { Fragment, useState } from 'react';

import { ReactComponent as ArrowLeftIcon } from '../../../assets/arrow-left.svg';
import { ConsumptionUnitEnum } from '../../../enum/ConsumptionUnitEnum';
import { ReactComponent as InformationIcon } from '../../../assets/information.svg';
import ModalInformation from '../../modals/ModalInformation';
import { NumberUtils } from '../../../common/numberUtils';
import UnitSelector from './UnitSelector';
import classNames from 'classnames';
import { useTranslation } from 'react-i18next';
import visualDayIcon from '../../../assets/visuel_day.svg';

const calculateProgression = (
  currentValue: number | null,
  previousValue: number | null
): number | null => {
  if (previousValue === 0 || previousValue === null || currentValue === null) {
    return null;
  }
  return Math.round(((currentValue - previousValue) / previousValue) * 100);
};

export interface ConsumptionDetailsProps {
  className?: string;
  title?: string;
  subtitle: string;
  mainValue?: string | undefined;
  hasEnableComparePreviousPeriod?: boolean;
  items?:
    | {
        label: string;
        value: string;
        badgeColor: string;
        stackKey: string;
      }[]
    | undefined;
  unit: ConsumptionUnitEnum;
  hasInformationIcon?: boolean;
  hasUnitSelector?: boolean;
  onChangeUnit?: (value: ConsumptionUnitEnum) => void;
}

export const groupedItemsByStackKey = (
  items:
    | {
        label: string;
        value: string;
        badgeColor: string;
        stackKey: string;
      }[]
    | undefined
) => {
  let groupedItems = {} as Record<
    string,
    {
      label: string;
      currentValue: string;
      previousValue?: string;
      progression?: number | null;
      badgeColor: string;
      stackKey: string;
    }[]
  >;
  items?.forEach((item: { label: string; value: string; badgeColor: string; stackKey: string }) => {
    let key = '';
    switch (item.label) {
      case 'common.sampling.previous.period':
      case 'common.injection.previous.period':
      case 'common.sampling':
      case 'common.injection':
        key = 'common.group.sampling-injection';
        break;
      case 'common.mid.hour':
      case 'common.full.hour':
        key = 'common.group.consumption';
        break;
      case 'common.shared':
      case 'common.network':
        key = 'common.group.shared-network';
        break;
      case 'common.injection.shared':
      case 'common.injection.network':
        key = 'common.injection-shared-network';
        break;
    }

    const prevItem = items?.find((it) => it.label === `${item.label}.previous.period`);

    const progression = calculateProgression(
      NumberUtils.toNumber(item.value),
      prevItem ? NumberUtils.toNumber(prevItem.value) : null
    );
    const isPreviousPeriodItem = item.label.includes('previous.period');
    if (!isPreviousPeriodItem && !groupedItems[key]) {
      groupedItems[key] = [];
      groupedItems[key].push({
        ...item,
        currentValue: item.value,
        previousValue: prevItem?.value,
        progression,
      });
    }
    if (!isPreviousPeriodItem && !groupedItems[key].find((i) => i.label === item.label)) {
      groupedItems[key].push({
        ...item,
        currentValue: item.value,
        previousValue: prevItem?.value,
        progression,
      });
    }
  });
  return groupedItems;
};
export default function ConsumptionDetails({
  className,
  title,
  subtitle,
  mainValue,
  items,
  unit,
  hasInformationIcon,
  onChangeUnit,
}: ConsumptionDetailsProps) {
  const { t } = useTranslation();
  const [isOpenModalInformation, setIsOpenModalInformation] = useState(false);
  const handleClickInfo = () => {
    setIsOpenModalInformation(!isOpenModalInformation);
  };

  const renderItem = (
    item: {
      label: string;
      currentValue: string;
      previousValue?: string;
      progression?: number | null;
      badgeColor: string;
      opacity?: number;
      stackKey: string;
    },
    index: number
  ) => (
    <div className="highcharts-tooltip__content__data__item" key={index}>
      <div className="highcharts-tooltip__content__data__item__label">
        <span
          className="highcharts-tooltip__content__data__item__badge"
          style={{ color: item.badgeColor, opacity: item.opacity ?? 1 }}
        >
          {'\u25CF'}
        </span>
        {t(item.label)}
      </div>
      {item.previousValue && (
        <Fragment>
          <div className="highcharts-tooltip__content__data__item__value highcharts-tooltip__content__data__item__value--previous">
            {item.previousValue}
          </div>
          <span className="highcharts-tooltip__content__data__item__separator">
            <ArrowLeftIcon width={24} height={24} />
          </span>
        </Fragment>
      )}
      <div className="highcharts-tooltip__content__data__item__value">
        {item.currentValue}
        <span className="highcharts-tooltip__content__data__item__unit">
          {ConsumptionUnitEnum.getSymbol(unit)}
        </span>
      </div>

      {item.progression !== null && item.progression !== 0 && (
        <div className="highcharts-tooltip__content__data__item__value highcharts-tooltip__content__data__item__value--progression">
          {renderProgression(item.progression!, item.label)}
        </div>
      )}
    </div>
  );

  const renderProgression = (progression: number, label: string) => {
    const integerProgression = Math.trunc(progression);
    if (integerProgression > 0 && label === 'common.sampling') {
      return (
        <div className="highcharts-tooltip__content__data__item__value__progression highcharts-tooltip__content__data__item__value__progression--not-good">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
            />
          </svg>
          <span style={{ marginLeft: 8 }}>{`+${integerProgression}%`}</span>
        </div>
      );
    } else if (integerProgression < 0 && label === 'common.sampling') {
      return (
        <div className="highcharts-tooltip__content__data__item__value__progression highcharts-tooltip__content__data__item__value__progression--good">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 6 9 12.75l4.286-4.286a11.948 11.948 0 0 1 4.306 6.43l.776 2.898m0 0 3.182-5.511m-3.182 5.51-5.511-3.181"
            />
          </svg>

          <span style={{ marginLeft: 8 }}>{`${integerProgression}%`}</span>
        </div>
      );
    } else if (integerProgression > 0 && label === 'common.injection') {
      return (
        <div className="highcharts-tooltip__content__data__item__value__progression highcharts-tooltip__content__data__item__value__progression--stable">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 18 9 11.25l4.306 4.306a11.95 11.95 0 0 1 5.814-5.518l2.74-1.22m0 0-5.94-2.281m5.94 2.28-2.28 5.941"
            />
          </svg>
          <span style={{ marginLeft: 8 }}>{`+${integerProgression}%`}</span>
        </div>
      );
    } else if (integerProgression < 0 && label === 'common.injection') {
      return (
        <div className="highcharts-tooltip__content__data__item__value__progression highcharts-tooltip__content__data__item__value__progression--stable">
          <svg
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
            stroke="currentColor"
            className="icon"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M2.25 6 9 12.75l4.286-4.286a11.948 11.948 0 0 1 4.306 6.43l.776 2.898m0 0 3.182-5.511m-3.182 5.51-5.511-3.181"
            />
          </svg>

          <span style={{ marginLeft: 8 }}>{`${integerProgression}%`}</span>
        </div>
      );
    }
  };

  return (
    <Fragment>
      <div className={classNames('highcharts-tooltip__content', className)}>
        {title && (
          <div className="highcharts-tooltip__content__title">
            <img
              alt="visual day icon"
              className="highcharts-tooltip__content__title__icon"
              src={visualDayIcon}
            />
            {title}
          </div>
        )}
        <div className="highcharts-tooltip__content__subtitle">
          {subtitle}
          {hasInformationIcon && (
            <InformationIcon
              onClick={handleClickInfo}
              className="icon highcharts-tooltip__content__subtitle__info-icon"
            />
          )}
        </div>
        {mainValue && (
          <div className="highcharts-tooltip__content__main-value">
            {mainValue}
            <span className="highcharts-tooltip__content__data__item__unit">
              {ConsumptionUnitEnum.getSymbol(unit)}
            </span>
          </div>
        )}
        <div className="highcharts-tooltip__content__data">
          {groupedItemsByStackKey(items) &&
            Object.keys(groupedItemsByStackKey(items)).map((itemKey, index) => (
              <div className="highcharts-tooltip__content__data__group" key={index}>
                <div
                  className={classNames({
                    'highcharts-tooltip__content__data__group__separator': index > 0,
                  })}
                ></div>
                {groupedItemsByStackKey(items)[itemKey].map((item, index) =>
                  renderItem(item, index)
                )}
              </div>
            ))}
        </div>
        {onChangeUnit && (
          <div className="highcharts-tooltip__content__footer">
            <UnitSelector value={unit} onChange={onChangeUnit} />
          </div>
        )}
      </div>
      {hasInformationIcon && (
        <ModalInformation isOpen={isOpenModalInformation} onClickAction={handleClickInfo}>
          <h2>{t('modal.consumption.info.title')}</h2>
          <p>{t('modal.consumption.info.description')}</p>
        </ModalInformation>
      )}
    </Fragment>
  );
}
